import { Column, DataTable } from "@/components/data-table";
import { useFilters } from "@/components/filters";
import { formatDate, formatNumber, formatString } from "@/shared/formats";
import { CuentaCorriente, CuentaCorrienteItem, CuentaCorrienteItemTipo } from "@/shared/types/cuenta-corriente";
import { Button, Stack, StackProps, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { getCuentaCorrienteItems, useGetCuentaCorrienteItemsQuery } from "../api/get-cuenta-corriente-items";
import { Form, useForm } from "@/components/form";
import { Drawer, ModalHeader, useOpenModal } from "@/components/modal";
import { AddCuentaCorrienteItemMutation } from "../api/add-cuenta-corriente-item";
import AddCuentaCorrienteItemForm, { addCuentaCorrienteItemFormSchema } from "./add-cuenta-corriente-item-form";
import { notify } from "@/shared/notification";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { SchemaFormHook } from "@/components/form";
import { ListPage } from "@/components/list-page";
import { useMemo } from "react";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";

interface ViewCuentaCorrienteProps extends StackProps {
    cuenta: CuentaCorriente
}

export default function ViewCuentaCorriente({
    cuenta,
    ...props
}: ViewCuentaCorrienteProps) {
    const openModal = useOpenModal();
    const { monedaBase } = useMonetizacion();

    const filterData = useFilters({
        useURL: false,
        filters: [
            { type: "dateRange", fromName: "desde", toName: "hasta", label: "Fecha" },
            { type: "pagination" }
        ]
    })

    const fetchData = useGetCuentaCorrienteItemsQuery(cuenta.personaId, filterData.filterData);

    const form = useForm({
        onSubmit: ({ value }) => {
            filterData.setFilterData({ ...filterData.filterData, ...value, page: 0 })
        },
        defaultValues: {
            desde: "",
            hasta: ""
        },
        submitOnce: false,
        closeModalOnSubmit: false,
    })

    const renderTipo = (row: CuentaCorrienteItem) => {
        if (row.tipo == CuentaCorrienteItemTipo.Cliente) return "Cliente";
        if (row.tipo == CuentaCorrienteItemTipo.Proveedor) return "Proveedor";
        if (row.tipo == CuentaCorrienteItemTipo.Vendedor) return "Vendedor";
        else return "Miscelaneo";
    }

    const columns = useMemo(() => [
        { header: "Fecha", render: row => formatDate(row.fecha, true), width: "auto" },
        { header: "Tipo", render: row => renderTipo(row), width: "auto", align: "center" },
        { header: "Descripción", render: row => formatString(row.descripcion) },
        { header: `Debe (${monedaBase.simbolo})`, render: row => formatNumber(row.debe), width: "auto", align: "right" },
        { header: `Haber (${monedaBase.simbolo})`, render: row => formatNumber(row.haber), width: "auto", align: "right" },
        { header: `Saldo (${monedaBase.simbolo})`, render: row => formatNumber(row.saldo), width: "auto", align: "right" },
    ] as Column<CuentaCorrienteItem>[], []);

    return <Stack gap={3} {...props}>
        <Stack gap={2} direction={"row"} justifyContent={"space-between"} flexWrap={"wrap"}>
            <Form form={form} gap={2} direction={"row"}>
                <Form.DatePicker name="desde" label="Desde" clearable size="small" />
                <Form.DatePicker name="hasta" label="Hasta" displayDateOffset={-1} clearable size="small" />
                <Form.SubmitButton size="small">Filtrar</Form.SubmitButton>
            </Form>
            <Stack gap={2} direction={"row"}>
                <Button variant="outlined" startIcon={<AddIcon />} size="small" onClick={() => openModal("addCuentaCorrienteItem")}>
                    Agregar Nueva Entrada
                </Button>
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("printCuentaCorriente")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("printCuentaCorriente")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csvCuentaCorriente")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsxCuentaCorriente")} />
                </ListPage.Header.Actions.ExportButton>
            </Stack>
        </Stack>
        <DataTable flex={1} minHeight={"55vh"}>
            <DataTable.Content
                fit="flex"
                columns={columns}
                rows={fetchData.data?.result}
                loading={fetchData.isFetching}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>
        <Stack direction={"row"} gap={4} justifyContent={"end"} alignItems={"end"}>
            <Typography variant="h6">Saldo: </Typography>
            <Typography variant="h5">{formatNumber(cuenta.saldo)} {monedaBase.simbolo}</Typography>
        </Stack>

        <Drawer code="addCuentaCorrienteItem">
            <ModalHeader>Nueva Entrada</ModalHeader>
            <AddCuentaCorrienteItemMutation personaId={cuenta.personaId}>
                {mutation => <SchemaFormHook
                    schema={addCuentaCorrienteItemFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify(`La entrada se ha agregado correctamente.`, { variant: "success" })
                    }}
                    submitOnce
                    closeModalOnSubmit
                >
                    {form => <AddCuentaCorrienteItemForm form={form} padding={3} />}
                </SchemaFormHook>}
            </AddCuentaCorrienteItemMutation>
        </Drawer>

        <PrintExportDialog
            code="printCuentaCorriente"
            columns={columns}
            filterData={filterData}
            fetchFn={query => getCuentaCorrienteItems(cuenta.personaId, query)}
            defaultFileTitle={`Cuenta Corriente - ${cuenta.personaNombre}`}
            additionalDisplayData={[
                { label: "Saldo", value: `${formatNumber(cuenta.saldo)} ${monedaBase.simbolo}` }
            ]}
        />

        <CsvExportDialog
            code="csvCuentaCorriente"
            columns={columns}
            filterData={filterData}
            fetchFn={query => getCuentaCorrienteItems(cuenta.personaId, query)}
            defaultFileTitle={`Cuenta Corriente - ${cuenta.personaNombre}`}
        />

        <XlsxExportDialog
            code="xlsxCuentaCorriente"
            columns={columns}
            filterData={filterData}
            fetchFn={query => getCuentaCorrienteItems(cuenta.personaId, query)}
            defaultFileTitle={`Cuenta Corriente - ${cuenta.personaNombre}`}
        />
    </Stack>
}